/**
 * Source Nodes - Constants with Citations
 *
 * These are "ground truth" values from authoritative sources.
 * They cannot be adjusted by the user.
 */

import { source, SourceNode } from '../../lib/computation-graph';

export const sources: Record<string, SourceNode> = {
  // Tax rates
  federalDepreciationRecaptureRate: source('federalDepreciationRecaptureRate', 0.25, {
    label: 'Federal Depreciation Recapture Rate',
    description: 'IRS Section 1250 unrecaptured gains rate',
    unit: 'percent',
    citation: {
      source: 'IRS Publication 544',
      url: 'https://www.irs.gov/publications/p544',
      description: 'Depreciation recapture is taxed at 25% for real property',
    },
  }),

  caStateTaxRate: source('caStateTaxRate', 12.3, {
    label: 'California Top Marginal Tax Rate',
    description: 'CA state income tax top bracket (13.3% over $1M)',
    unit: 'percent',
    citation: {
      source: 'California Franchise Tax Board',
      url: 'https://www.ftb.ca.gov/file/personal/tax-rates.html',
      description: 'California has a progressive tax with 12.3% at the top bracket',
    },
  }),

  prop13MaxGrowth: source('prop13MaxGrowth', 2, {
    label: 'Prop 13 Max Assessment Growth',
    description: 'Maximum annual increase in assessed value under Prop 13',
    unit: 'percent',
    citation: {
      source: 'California State Board of Equalization',
      url: 'https://www.boe.ca.gov/proptaxes/prop13.htm',
      description: 'Prop 13 limits assessed value increases to 2% per year',
    },
  }),

  sp500DividendYield: source('sp500DividendYield', 1.3, {
    label: 'S&P 500 Dividend Yield',
    description: 'Current approximate dividend yield of S&P 500',
    unit: 'percent',
    citation: {
      source: 'S&P Dow Jones Indices',
      url: 'https://www.spglobal.com/spdji/en/indices/equity/sp-500/',
      description: 'Historical dividend yield around 1.3-1.5%',
    },
  }),

  bondYieldAssumption: source('bondYieldAssumption', 4, {
    label: 'Bond Yield Assumption',
    description: 'Assumed yield on bond portion of portfolio',
    unit: 'percent',
    citation: {
      source: 'Federal Reserve Economic Data',
      url: 'https://fred.stlouisfed.org/series/DGS10',
      description: 'Based on 10-year Treasury yields plus corporate spread',
    },
  }),

  residentialDepreciationYears: source('residentialDepreciationYears', 27.5, {
    label: 'Residential Depreciation Period',
    description: 'IRS depreciation period for residential rental property',
    unit: 'years',
    citation: {
      source: 'IRS Publication 946',
      url: 'https://www.irs.gov/publications/p946',
      description: 'Residential rental property is depreciated over 27.5 years',
    },
  }),

  coastalBuildingRatio: source('coastalBuildingRatio', 0.25, {
    label: 'Coastal CA Building-to-Land Ratio',
    description: 'Typical building value as percentage of total for coastal properties',
    unit: 'ratio',
    citation: {
      source: 'County Assessor Practices',
      url: 'https://www.ocgov.com/gov/assessor',
      description: 'Coastal CA properties typically have 25% building / 75% land value split',
    },
  }),

  inflationRate: source('inflationRate', 2.5, {
    label: 'Expected Inflation Rate',
    description: 'Long-term inflation expectation',
    unit: 'percent',
    citation: {
      source: 'Federal Reserve',
      url: 'https://www.federalreserve.gov/monetarypolicy/faq.htm',
      description: 'Fed targets 2% inflation; 2.5% is a conservative assumption',
    },
  }),
};

export default sources;
